/**
 * Next Step Suggester — after a workflow completes, suggest what
 * the user should do next based on what just ran and the current
 * workspace state.
 *
 * @module gibbs/knowledge/nextStepSuggester
 */

/**
 * Generate a contextual next-step suggestion based on a completed
 * workflow plan and workspace state.
 *
 * @param {Object} plan - The completed ActionPlan
 * @param {Object} workspace - App.Workspace
 * @returns {string|null} Suggestion message, or null if none
 */
export function suggestNextStep(plan, workspace) {
    if (!plan || !plan.actions || plan.actions.length === 0) {
        return null;
    }

    const actionIds = plan.actions.map((a) => a.actionId);
    const lastAction = actionIds[actionIds.length - 1];

    // What compounds exist now?
    const compounds = workspace?.getLoadedCompounds?.() || [];
    const compoundCount = compounds.length;

    // Does the workspace have energies?
    const hasEnergies = compounds.some(
        (c) => c.energyInfo?.getEnergyScore?.() != null
    );

    // Best score if available
    let bestScore = null;
    let bestIdx = null;
    if (hasEnergies) {
        for (let i = 0; i < compounds.length; i++) {
            const score = compounds[i].energyInfo?.getEnergyScore?.();
            if (score != null && (bestScore == null || score < bestScore)) {
                bestScore = score;
                bestIdx = i + 1;
            }
        }
    }

    // Fragment map available?
    const hasFragMap = (() => {
        const cd = workspace?.firstCaseData?.();
        if (!cd) return false;
        const fi = cd.getAvailableFragmentInfo?.();
        return fi && typeof fi.items === 'function'
            && fi.items().length > 0;
    })();

    // ─── Suggestions based on what just completed ──────────────────

    // After protein load (acquire phase)
    if (actionIds.includes('load-protein-from-pdb')
        && actionIds.length === 1) {
        const parts = ['Protein loaded.'];
        if (hasFragMap) {
            parts.push(
                'Say **"discover hits"** to run fragment-based hit '
                + 'discovery.'
            );
        }
        if (compoundCount > 0) {
            parts.push(
                'Say **"dock and score"** to evaluate the loaded '
                + 'compounds, **"screen my library"** for virtual '
                + 'screening, or **"generate new compounds"** for '
                + 'de novo design.'
            );
        } else {
            parts.push(
                'Load a compound (sketcher, import, or starter '
                + 'compounds) to get started.'
            );
        }
        return parts.join(' ');
    }

    // After hit discovery (grow + minimize + score)
    //
    // MUST be checked BEFORE the dock-and-score branch below: hit
    // discovery plans include BOTH grow-from-all-atoms AND
    // predict-gnina-scores, so the dock-and-score branch (which
    // matches on `predict-gnina-scores`) would otherwise hijack the
    // suggestion and the §3 corpus's "N compounds now in workspace"
    // callout would never fire. See routing-gap #7 (resolved
    // 2026-05-03).
    if (actionIds.includes('grow-from-all-atoms')) {
        const parts = [];
        if (compoundCount > 1) {
            parts.push(
                `**${compoundCount} compounds** now in the workspace.`
            );
        }
        if (bestScore != null) {
            parts.push(
                `Best score: Compound ${bestIdx} `
                + `(${bestScore.toFixed(1)} kcal/mol).`
            );
        }
        parts.push('Next steps:');
        parts.push('- **"show scores"** to rank all compounds');
        parts.push(
            '- **"show interactions"** to analyze the best hit'
        );
        parts.push(
            '- **"dock and score"** to re-evaluate with full docking'
        );
        return parts.join('\n');
    }

    // After dock-and-score
    if (actionIds.includes('dock-compound')
        || actionIds.includes('predict-gnina-scores')) {
        const parts = [];
        if (bestScore != null) {
            parts.push(
                `Best score: Compound ${bestIdx} `
                + `(${bestScore.toFixed(1)} kcal/mol).`
            );
        }
        parts.push('Next steps:');
        parts.push('- **"show interactions"** to analyze the binding');
        if (hasFragMap) {
            parts.push(
                '- **"discover hits"** to grow fragments and find '
                + 'new compounds'
            );
        }
        parts.push('- **"show scores"** to see all compound rankings');
        return parts.join('\n');
    }

    // After virtual screening (library screen job)
    if (actionIds.includes('job-library-screen')) {
        const parts = [];
        parts.push('Library screening job submitted.');
        parts.push('Next steps:');
        parts.push(
            '- **"show scores"** to view results when the job completes'
        );
        parts.push(
            '- **"show interactions"** to analyze the best hit'
        );
        parts.push(
            '- **"generate new compounds"** to run de novo generation '
            + 'on the best hits'
        );
        return parts.join('\n');
    }

    // After de novo generation (REINVENT job)
    if (actionIds.includes('job-reinvent')) {
        const parts = [];
        parts.push('REINVENT generation job submitted.');
        parts.push('Next steps:');
        parts.push(
            '- **"show scores"** to review generated molecules '
            + 'when the job completes'
        );
        parts.push(
            '- **"screen my library"** to screen the generated '
            + 'compounds against the target'
        );
        parts.push(
            '- **"dock and score"** to evaluate individual compounds'
        );
        return parts.join('\n');
    }

    // After energy minimize only
    if (lastAction === 'energy-minimize' && !actionIds.includes('dock-compound')) {
        const parts = [];
        if (bestScore != null) {
            parts.push(
                `Minimization complete. Best score: Compound ${bestIdx} `
                + `(${bestScore.toFixed(1)} kcal/mol).`
            );
        } else {
            parts.push('Minimization complete.');
        }
        parts.push('- **"show scores"** to see energy rankings');
        parts.push('- **"show interactions"** to analyze binding');
        return parts.join('\n');
    }

    // Generic fallback
    if (compoundCount > 0 && hasEnergies) {
        return 'Workflow complete. Say **"show scores"** to see '
            + 'compound rankings, or **"show interactions"** to '
            + 'analyze the binding.';
    }

    return null;
}
